export const sleep = <TResult>(ms: number, result?: TResult) => new Promise<typeof result>((r) => setTimeout(() => r(result), ms));

export function getSearchDictionary({ search }: Location, toLowerCaseKeys = false): Record<string, string> {
    return !search
        ? {}
        : Object.fromEntries(
              search
                  .slice(1)
                  .split('&')
                  .map((c) => (toLowerCaseKeys ? c.split('=').map((a, i) => (i == 0 ? a.toLocaleLowerCase() : a)) : c.split('=')))
          );
}

/**
 * @param location window location object
 * @param key Case insensitive Key to look for in querystring
 * @returns Value of querystring parameter.
 */
export function getQuerystringValue(location: Location, key: string): string {
    const value = getSearchDictionary(location, true)[key.toLocaleLowerCase()];
    if (!value) {
        throw new Error(`Key not present in querystring: '${key}'`);
    }
    return value;
}

export function dictionaryToSearch(dictionary: Record<string, string>): string {
    const params = Object.entries(dictionary)
        .map(([k, v]) => `${k}=${encodeURI(v)}`)
        .join('&');
    return params ? `?${params}` : '';
}

export function hashCode(obj: unknown) {
    if (obj === undefined || obj === null) {
        return 0;
    }
    const string = obj.toString();
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        const code = string.charCodeAt(i);
        hash = (hash << 5) - hash + code;
        hash = hash & hash;
    }
    return hash;
}
export function equals<T>(a: T, b: T) {
    return hashCode(a) == hashCode(b);
}

export function arraysEqual<T>(a: T[], b: T[]) {
    if (a.length != b.length) {
        return false;
    }
    return a.every((v, i) => b[i] === v);
}

export function capitalize(str: string): string {
    if (!str) {
        return str;
    }

    return `${str[0].toUpperCase()}${str.slice(1)}`;
}

/** Takes an object, and convert all keys with '.' to nested objects.
 * @example dotToObject({ "prop.no": 42 }) --> { prop: { no: 42 } }
 */
export function dotToObject<
    TObject extends {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [field: string]: any;
    }
>(data: TObject) {
    function index(parent: TObject, key: string, value: unknown) {
        const [mainKey, ...children] = key.split('.');
        parent[mainKey as keyof typeof parent] = parent[mainKey] || {};

        if (children.length === 1) {
            parent[mainKey][children[0]] = value;
        } else {
            index(parent[mainKey] as TObject, children.join('.'), value);
        }
    }

    const result = Object.entries(data).reduce((acc, [key, value]) => {
        if (key.includes('.')) {
            index(acc, key, value);
        } else {
            acc[key as keyof typeof acc] = value;
        }

        return acc;
    }, {} as TObject);
    return result;
}
const RE_INDEX = /^(.+)\[(\d+)\]$/;

export function dotPropsToObjects(obj: unknown) {
    if (isObject(obj)) {
        return Object.entries(obj).reduce((mem, [key, value]) => {
            const [root, ...children] = key.split('.');
            const indexerMatch = RE_INDEX.exec(root);
            if (indexerMatch) {
                const name = indexerMatch[1];
                const index = parseInt(indexerMatch[2], 10);

                const arr = <unknown[]>mem[name] || [];
                arr[index] = mergeDeep(arr[index] || {}, dotPropsToObjects(children.length ? { [children.join('.')]: value } : value));
                mem[name] = arr;
            } else if (root == key) {
                mem[key] = dotPropsToObjects(value);
            } else {
                mem[root] = mergeDeep(mem[root] || {}, dotPropsToObjects(children.length ? { [children.join('.')]: value } : value));
            }
            return mem;
        }, {} as Record<string, unknown>);
    }
    return obj;
}

export function flatten<T extends NonNullable<unknown>>(obj: T, separator = '.') {
    return Object.fromEntries(Object.entries(obj).flatMap((entry) => [...flattenEntry(entry, separator)]));
}

type Entry = [key: string, value: unknown];

function* flattenEntry([k, v]: Entry, separator: string): Generator<Entry> {
    if (typeof v == 'object' && v !== null) {
        if (v.constructor.name == 'Object') {
            for (const entry of Object.entries(v)) {
                const key = `${k}${separator}${entry[0]}`;
                yield* flattenEntry([key, entry[1]], separator);
            }
            return;
        }
        if (v instanceof Array) {
            for (let i = 0; i < v.length; i++) {
                const key = `${k}[${i}]`;
                yield* flattenEntry([key, v[i]], separator);
            }
            return;
        }
    }
    yield [k, v];
}

export function isEmptyObject(obj: unknown): obj is Record<string, never> {
    if (typeof obj == 'object' && obj != null) {
        return Object.keys(obj).length == 0 && Object.getPrototypeOf(obj) === Object.prototype;
    }
    return false;
}

function isObject(obj: unknown): obj is object {
    return typeof obj == 'object' && obj != null && Object.getPrototypeOf(obj) === Object.prototype;
}

export function mergeDeep(target: unknown, source: unknown) {
    if (!isObject(target) || !isObject(source)) {
        return isObject(source) ? { ...source } : source;
    }
    const merged = { ...target } as Record<string, unknown>;
    for (const [key, value] of Object.entries(source)) {
        const targetValue = merged[key];
        if (Array.isArray(targetValue) && Array.isArray(value)) {
            merged[key] = value.reduce((m, c, i) => {
                if (m[i]) {
                    m[i] = mergeDeep(m[i], c);
                } else {
                    m[i] = c;
                }
                return m;
            }, targetValue);
        } else if (isObject(value)) {
            merged[key] = mergeDeep(merged[key], value);
        } else {
            merged[key] = value;
        }
    }
    return merged;
}

export function valueAt<T extends object, P extends PathsOfType<T> | string, R extends P extends PathsOfType<T> ? TypeAtPath<T, P> : undefined>(
    obj: T,
    path: P
): R {
    const parts = path.split('.');
    return parts.reduce((mem, part) => mem && mem[part as keyof typeof mem], obj as unknown) as R;
}

export function isSibblingTo(pathA: string, pathB: string) {
    const partsA = pathA.split('.');
    const partsB = pathB.split('.');
    if (partsA.length !== partsB.length) {
        return false;
    }
    return partsA.slice(0, partsA.length - 1).every((v, i) => v == partsB[i]);
}

/** Random, but not guaranteed unique! */
export function randomHex() {
    return Math.round(Math.random() * 17_777_215).toString(16);
}

export function pluck<T extends NonNullable<unknown>, A extends keyof T>(obj: T, ...args: A[]) {
    return Object.fromEntries(Object.entries(obj).filter(([key]) => args.includes(key as A))) as {
        [k in (typeof args)[number]]: T[k];
    };
}

export function unpluck<T extends NonNullable<unknown>, A extends keyof T>(obj: T, ...args: A[]) {
    return Object.fromEntries(Object.entries(obj).filter(([key]) => !args.includes(key as A))) as {
        [k in Exclude<A, typeof args[number]>]: T[k];
    };
}

export function nullAsEmpty<T>(obj: T) {
    if (obj === null || obj === undefined) {
        return {};
    }
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === null || v === undefined ? '' : v])) as {
        [k in keyof T]: T[k] extends null ? '' : T[k];
    };
}
